import {
  KoButton,
  KoForm,
  KoModal,
  KoNano,
  KoText,
  KoToast,
  KoTag,
  KoTabs,
  KoTextArea,
  KoEmpty,
  KoAvatar
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { PR_STATUS_CONFIG, PR_GOVERNANCE_CHECKS, PR_AUTOMATED_CHECKS } from '../constants';
import PRCrud from './crud/PRCrud';
import GitProviderCrud from './crud/GitProviderCrud';
import DiffViewer from './DiffViewer';
import ReleaseSubNav from './ReleaseSubNav';
import styles from './releases.module.scss';

const MOCK_PRS = [
  {
    prId: 'PR-001',
    prNumber: 142,
    title: 'feat: Add new dashboard analytics widgets',
    description: 'Implements real-time analytics widgets including revenue chart, user activity heatmap, and conversion funnel. Integrates with the new metrics API.',
    ticketId: 'KWP-882',
    status: 'approved',
    author: 'alice',
    authorName: 'Alice Park',
    fromBranch: 'feature/dashboard-widgets',
    intoBranch: 'release/v4.2.0',
    filesChanged: 24,
    additions: 1842,
    deletions: 312,
    idleDays: 0,
    externalUrl: '',
    gitProvider: 'github',
    reviewers: [
      { userId: 'u1', name: 'Bob Chen', role: 'Engineering Lead', decision: 'approved' },
      { userId: 'u2', name: 'Sarah Kim', role: 'Senior Engineer', decision: 'approved' }
    ],
    governanceChecks: { linked_ticket: true, linked_release: true, description: true, risk_note: true, migration_note: false, feature_flag_ref: true },
    automatedChecks: { reviewer_approvals: true, ci_passing: true, unit_tests: true, coverage_threshold: true, static_analysis: true, dependency_scan: true, secrets_scan: true, license_compliance: true }
  },
  {
    prId: 'PR-002',
    prNumber: 143,
    title: 'fix: Resolve authentication token refresh race condition',
    description: 'Fixes a race condition where concurrent API calls during token refresh could cause 401 errors. Implements a request queue that holds pending requests until refresh completes.',
    ticketId: 'KWP-891',
    status: 'in_review',
    author: 'bob',
    authorName: 'Bob Chen',
    fromBranch: 'fix/auth-token-refresh',
    intoBranch: 'release/v4.2.0',
    filesChanged: 8,
    additions: 156,
    deletions: 42,
    idleDays: 2,
    externalUrl: '',
    gitProvider: 'github',
    reviewers: [
      { userId: 'u3', name: 'John Doe', role: 'Tech Lead', decision: null },
      { userId: 'u1', name: 'Alice Park', role: 'Senior Engineer', decision: 'approved' }
    ],
    governanceChecks: { linked_ticket: true, linked_release: true, description: true, risk_note: true, migration_note: false, feature_flag_ref: false },
    automatedChecks: { reviewer_approvals: false, ci_passing: true, unit_tests: true, coverage_threshold: true, static_analysis: true, dependency_scan: true, secrets_scan: true, license_compliance: true }
  },
  {
    prId: 'PR-003',
    prNumber: 144,
    title: 'refactor: Migrate user service to new API layer',
    description: 'Migrates the user service from legacy REST client to the new KoAPI layer with retry logic, circuit breaker, and request deduplication.',
    ticketId: 'KWP-905',
    status: 'changes_requested',
    author: 'sarah',
    authorName: 'Sarah Kim',
    fromBranch: 'refactor/user-service-migration',
    intoBranch: 'release/v4.2.0',
    filesChanged: 32,
    additions: 890,
    deletions: 1204,
    idleDays: 3,
    externalUrl: '',
    gitProvider: 'github',
    reviewers: [
      { userId: 'u3', name: 'John Doe', role: 'Tech Lead', decision: 'changes_requested' },
      { userId: 'u1', name: 'Bob Chen', role: 'Engineering Lead', decision: null }
    ],
    governanceChecks: { linked_ticket: true, linked_release: true, description: true, risk_note: true, migration_note: true, feature_flag_ref: false },
    automatedChecks: { reviewer_approvals: false, ci_passing: false, unit_tests: false, coverage_threshold: true, static_analysis: true, dependency_scan: true, secrets_scan: true, license_compliance: true }
  },
  {
    prId: 'PR-004',
    prNumber: 145,
    title: 'feat: Implement notification preferences panel',
    description: 'Adds user notification preferences with granular controls for email, push, and in-app notifications per event category.',
    ticketId: 'KWP-912',
    status: 'open',
    author: 'alice',
    authorName: 'Alice Park',
    fromBranch: 'feature/notification-prefs',
    intoBranch: 'release/v4.2.0',
    filesChanged: 15,
    additions: 620,
    deletions: 45,
    idleDays: 0,
    externalUrl: '',
    gitProvider: 'github',
    reviewers: [],
    governanceChecks: { linked_ticket: true, linked_release: true, description: true, risk_note: false, migration_note: false, feature_flag_ref: true },
    automatedChecks: { reviewer_approvals: false, ci_passing: true, unit_tests: true, coverage_threshold: false, static_analysis: true, dependency_scan: true, secrets_scan: true, license_compliance: true }
  },
  {
    prId: 'PR-005',
    prNumber: 140,
    title: 'chore: Update dependency versions for Q1 security audit',
    description: 'Bumps all outdated dependencies flagged by quarterly security audit. Includes major version bumps for axios, react-router, and lodash.',
    ticketId: 'KWP-870',
    status: 'merged',
    author: 'bob',
    authorName: 'Bob Chen',
    fromBranch: 'chore/q1-dep-updates',
    intoBranch: 'release/v4.2.0',
    filesChanged: 3,
    additions: 412,
    deletions: 380,
    idleDays: 0,
    externalUrl: '',
    gitProvider: 'github',
    reviewers: [
      { userId: 'u1', name: 'Alice Park', role: 'Senior Engineer', decision: 'approved' },
      { userId: 'u3', name: 'John Doe', role: 'Tech Lead', decision: 'approved' }
    ],
    governanceChecks: { linked_ticket: true, linked_release: true, description: true, risk_note: true, migration_note: false, feature_flag_ref: false },
    automatedChecks: { reviewer_approvals: true, ci_passing: true, unit_tests: true, coverage_threshold: true, static_analysis: true, dependency_scan: true, secrets_scan: true, license_compliance: true }
  }
];

const MOCK_COMMENTS = [
  { commentId: 'c1', authorName: 'John Doe', content: 'Please add error boundary around the chart component.', createdAt: '2026-02-22 14:30', resolved: true },
  { commentId: 'c2', authorName: 'Bob Chen', content: 'The retry logic should use exponential backoff here.', createdAt: '2026-02-23 09:15', resolved: false, codeLine: 'await retry(fn, { maxRetries: 3 })' },
  { commentId: 'c3', authorName: 'Alice Park', content: 'LGTM! Clean implementation.', createdAt: '2026-02-23 11:00', resolved: false }
];

const MOCK_GIT_CONNECTIONS = [
  { connectionId: 'gc-1', provider: 'github', name: 'kwapio/platform' }
];

const MOCK_DIFF_DATA = {
  files: [
    {
      filename: 'src/services/payment.js',
      status: 'modified',
      additions: 14,
      deletions: 4,
      chunks: [{
        header: '@@ -42,12 +42,22 @@ class PaymentService {',
        lines: [
          { type: 'context', content: '  async processPayment(order) {', oldLine: 42, newLine: 42 },
          { type: 'context', content: '    const customer = await this.getCustomer(order.customerId);', oldLine: 43, newLine: 43 },
          { type: 'remove', content: '    const result = await this.gateway.charge(order.total);', oldLine: 44 },
          { type: 'remove', content: '    return { success: result.ok, transactionId: result.id };', oldLine: 45 },
          { type: 'add', content: '    const validated = await this.validateOrder(order);', newLine: 44 },
          { type: 'add', content: '    const idempotencyKey = `pay_${order.id}_${Date.now()}`;', newLine: 45 },
          { type: 'add', content: '    const result = await this.gateway.charge(validated.total, {', newLine: 46 },
          { type: 'add', content: '      idempotencyKey,', newLine: 47 },
          { type: 'add', content: '      currency: validated.currency,', newLine: 48 },
          { type: 'add', content: '      metadata: { orderId: order.id, customerId: customer.id }', newLine: 49 },
          { type: 'add', content: '    });', newLine: 50 },
          { type: 'add', content: '', newLine: 51 },
          { type: 'add', content: '    if (!result.ok) {', newLine: 52 },
          { type: 'add', content: '      await this.handleFailedPayment(order, result);', newLine: 53 },
          { type: 'add', content: '      return { success: false, error: result.error };', newLine: 54 },
          { type: 'add', content: '    }', newLine: 55 },
          { type: 'add', content: '', newLine: 56 },
          { type: 'add', content: '    return { success: true, transactionId: result.id };', newLine: 57 },
          { type: 'context', content: '  }', oldLine: 46, newLine: 58 },
          { type: 'context', content: '', oldLine: 47, newLine: 59 }
        ]
      }]
    },
    {
      filename: 'src/components/Dashboard/AnalyticsWidget.jsx',
      status: 'added',
      additions: 18,
      deletions: 0,
      chunks: [{
        header: '@@ -0,0 +1,18 @@',
        lines: [
          { type: 'add', content: "import { useEffect, useState } from 'react';", newLine: 1 },
          { type: 'add', content: "import { fetchMetrics } from '../../services/analytics';", newLine: 2 },
          { type: 'add', content: '', newLine: 3 },
          { type: 'add', content: 'export default function AnalyticsWidget({ timeRange }) {', newLine: 4 },
          { type: 'add', content: '  const [metrics, setMetrics] = useState(null);', newLine: 5 },
          { type: 'add', content: '', newLine: 6 },
          { type: 'add', content: '  useEffect(() => {', newLine: 7 },
          { type: 'add', content: '    fetchMetrics(timeRange).then(setMetrics);', newLine: 8 },
          { type: 'add', content: '  }, [timeRange]);', newLine: 9 },
          { type: 'add', content: '', newLine: 10 },
          { type: 'add', content: '  if (!metrics) return <Skeleton />;', newLine: 11 },
          { type: 'add', content: '', newLine: 12 },
          { type: 'add', content: '  return (', newLine: 13 },
          { type: 'add', content: '    <div className="analytics-widget">', newLine: 14 },
          { type: 'add', content: '      <MetricCard label="Revenue" value={metrics.revenue} />', newLine: 15 },
          { type: 'add', content: '      <MetricCard label="Users" value={metrics.activeUsers} />', newLine: 16 },
          { type: 'add', content: '    </div>', newLine: 17 },
          { type: 'add', content: '  );', newLine: 18 }
        ]
      }]
    },
    {
      filename: 'src/middleware/auth.js',
      status: 'modified',
      additions: 6,
      deletions: 3,
      chunks: [{
        header: '@@ -18,9 +18,12 @@ export function authMiddleware(req, res, next) {',
        lines: [
          { type: 'context', content: '  const token = req.headers.authorization?.split(" ")[1];', oldLine: 18, newLine: 18 },
          { type: 'context', content: '', oldLine: 19, newLine: 19 },
          { type: 'remove', content: '  if (!token) return res.status(401).json({ error: "No token" });', oldLine: 20 },
          { type: 'remove', content: '  const decoded = jwt.verify(token, SECRET);', oldLine: 21 },
          { type: 'remove', content: '  req.user = decoded;', oldLine: 22 },
          { type: 'add', content: '  if (!token) {', newLine: 20 },
          { type: 'add', content: '    return res.status(401).json({ error: "Missing authorization token" });', newLine: 21 },
          { type: 'add', content: '  }', newLine: 22 },
          { type: 'add', content: '', newLine: 23 },
          { type: 'add', content: '  const decoded = jwt.verify(token, SECRET, { algorithms: ["RS256"] });', newLine: 24 },
          { type: 'add', content: '  req.user = { ...decoded, authenticatedAt: Date.now() };', newLine: 25 },
          { type: 'context', content: '  next();', oldLine: 23, newLine: 26 },
          { type: 'context', content: '}', oldLine: 24, newLine: 27 }
        ]
      }]
    }
  ]
};

function PullRequestCard({ pr, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [diffOpen, setDiffOpen] = useState(false);

  const statusCfg = PR_STATUS_CONFIG[pr.status] || PR_STATUS_CONFIG.open;

  const handleExpand = () => {
    if (!expanded) setComments(MOCK_COMMENTS);
    setExpanded((v) => !v);
  };

  const handleViewDiff = () => {
    setDiffOpen(true);
  };

  const handleApprove = () => {
    KoToast.success(`PR #${pr.prNumber} approved!`);
    onRefresh(pr.prId, 'approved');
  };

  const handleReject = () => {
    if (!commentText.trim()) {
      KoToast.error('Please provide a reason for rejection.');
      return;
    }
    KoToast.success('Changes requested.');
    setCommentText('');
    onRefresh(pr.prId, 'changes_requested');
  };

  const handleMerge = () => {
    KoToast.success(`PR #${pr.prNumber} merged!`);
    onRefresh(pr.prId, 'merged');
  };

  const postComment = () => {
    if (!commentText.trim()) return;
    setComments((prev) => [...prev, {
      commentId: KoNano('c'),
      authorName: 'You',
      content: commentText,
      createdAt: new Date().toLocaleString(),
      resolved: false
    }]);
    setCommentText('');
    KoToast.success('Comment posted.');
  };

  const resolveComment = (commentId) => {
    setComments((prev) => prev.map((c) => c.commentId === commentId ? { ...c, resolved: true } : c));
    KoToast.success('Comment resolved.');
  };

  const canMerge = pr.status === 'approved';
  const isBlocked = pr.status === 'changes_requested';
  const governanceChecks = pr.governanceChecks || {};
  const automatedChecks = pr.automatedChecks || {};
  const allChecksPassed = PR_AUTOMATED_CHECKS.every(c => automatedChecks[c.key]);

  return (
    <>
      <div className={`${styles.prCard} ${isBlocked ? styles.prCardBlocked : ''}`}>
        <div className={styles.prHeader} onClick={handleExpand}>
          <div className={styles.prHeaderLeft}>
            <KoText className={styles.prNum} text={`#${pr.prNumber}`} />
            <div className={styles.prTitleWrap}>
              <KoText className={styles.prTitle} text={pr.title} />
              {pr.ticketId && <span className={styles.ticketChip}>{pr.ticketId}</span>}
            </div>
          </div>
          <div className={styles.prHeaderRight}>
            {!allChecksPassed && <KoTag color='error'>Checks Failing</KoTag>}
            <KoTag color={statusCfg.color}>{statusCfg.label}</KoTag>
            {pr.idleDays > 1 && <KoTag color='error'>{pr.idleDays}d idle</KoTag>}
            <KoAvatar size={22} options={[{ name: pr.authorName }]} />
            <KoIcon name={expanded ? 'ChevronUpOutlined' : 'ChevronDownOutlined'} width={14} height={14} />
          </div>
        </div>

        {expanded && (
          <div className={styles.prBody}>
            <div className={styles.prMeta}>
              <span className={styles.metaItem}>
                <KoIcon name='FileOutlined' width={12} height={12} />
                <KoText text={`${pr.filesChanged || 0} files`} />
              </span>
              <span className={`${styles.metaItem} ${styles.additions}`}>+{pr.additions || 0}</span>
              <span className={`${styles.metaItem} ${styles.deletions}`}>-{pr.deletions || 0}</span>
              {pr.fromBranch && (
                <span className={styles.metaItem}>
                  <KoIcon name='BranchOutlined' width={12} height={12} />
                  <KoText className={styles.branchTextSmall} text={`${pr.fromBranch} -> ${pr.intoBranch}`} />
                </span>
              )}
            </div>

            {pr.description && (
              <div className={styles.prDescription}>
                <KoText text={pr.description} />
              </div>
            )}

            {isBlocked && (
              <div className={styles.blockedBanner}>
                <KoIcon name='WarningOutlined' width={14} height={14} />
                <KoText text='Changes requested — developer must address all comments before re-review' />
              </div>
            )}

            <div className={styles.checksSection}>
              <div className={styles.checksCol}>
                <KoText className={styles.sectionLabel} text='GOVERNANCE CHECKS' />
                <div className={styles.checksList}>
                  {PR_GOVERNANCE_CHECKS.map(c => (
                    <div key={c.key} className={styles.checkItem}>
                      <KoIcon
                        name={governanceChecks[c.key] ? 'CheckCircleOutlined' : 'ClockCircleOutlined'}
                        width={14} height={14}
                        color={governanceChecks[c.key] ? 'var(--palette-success-main)' : 'var(--palette-text-disabled)'}
                      />
                      <KoText text={c.label} />
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.checksCol}>
                <KoText className={styles.sectionLabel} text='AUTOMATED CHECKS' />
                <div className={styles.checksList}>
                  {PR_AUTOMATED_CHECKS.map(c => (
                    <div key={c.key} className={styles.checkItem}>
                      <KoIcon
                        name={automatedChecks[c.key] ? 'CheckCircleOutlined' : 'CloseCircleOutlined'}
                        width={14} height={14}
                        color={automatedChecks[c.key] ? 'var(--palette-success-main)' : 'var(--palette-error-main)'}
                      />
                      <KoText text={c.label} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.prTwoCols}>
              <div className={styles.prComments}>
                <KoText className={styles.sectionLabel} text='REVIEW COMMENTS' />
                {comments.length === 0
                  ? <KoText className={styles.emptyNote} text='No comments yet' />
                  : comments.map((c) => (
                    <div key={c.commentId} className={`${styles.comment} ${c.resolved ? styles.commentResolved : ''}`}>
                      <KoAvatar size={24} options={[{ name: c.authorName }]} />
                      <div className={styles.commentBubble}>
                        <div className={styles.commentMeta}>
                          <KoText className={styles.commentAuthor} text={c.authorName} />
                          <KoText className={styles.commentTime} text={c.createdAt} />
                        </div>
                        <div className={styles.commentContent}>
                          <KoText text={c.content} />
                          {c.codeLine && <div className={styles.codeLine}><code>{c.codeLine}</code></div>}
                        </div>
                        {!c.resolved && (
                          <div className={styles.commentActions}>
                            <KoButton className='btn' label='Resolve' type='text' onClick={() => resolveComment(c.commentId)} />
                          </div>
                        )}
                        {c.resolved && <KoTag color='success' style={{ marginTop: 4 }}>Resolved</KoTag>}
                      </div>
                    </div>
                  ))
                }
              </div>

              <div className={styles.prActions}>
                <KoText className={styles.sectionLabel} text='LEAD REVIEW' />
                <div className={styles.reviewerList}>
                  {(pr.reviewers || []).map((r) => (
                    <div key={r.userId} className={styles.reviewerRow}>
                      <KoAvatar size={24} options={[{ name: r.name }]} />
                      <div className={styles.reviewerInfo}>
                        <KoText className={styles.reviewerName} text={r.name} />
                        <KoText className={styles.reviewerRole} text={r.role} />
                      </div>
                      <KoTag color={
                        r.decision === 'approved' ? 'success' :
                          r.decision === 'changes_requested' ? 'error' : 'default'
                      }>
                        {r.decision === 'approved' ? 'Approved' :
                          r.decision === 'changes_requested' ? 'Changes Req.' : 'Pending'}
                      </KoTag>
                    </div>
                  ))}
                </div>

                <div className={styles.addComment}>
                  <KoText className={styles.sectionLabel} text='ADD COMMENT / FEEDBACK' />
                  <KoTextArea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder='Leave review feedback or reason for rejection...'
                    rows={3}
                    className={styles.commentInput}
                  />
                  <div className={styles.commentSubmitRow}>
                    <KoButton label='Post Comment' onClick={postComment} />
                  </div>
                </div>

                <div className={styles.leadActions}>
                  <KoButton
                    type='primary'
                    label={canMerge ? 'Merge PR' : 'Approve'}
                    onClick={canMerge ? handleMerge : handleApprove}
                  />
                  <KoButton
                    danger
                    label='Request Changes'
                    onClick={handleReject}
                  />
                </div>
              </div>
            </div>

            <div className={styles.prActionBar}>
              <KoButton
                icon={<KoIcon name='DiffOutlined' width={14} height={14} />}
                label='View Diff'
                onClick={handleViewDiff}
              />
            </div>
          </div>
        )}
      </div>

      <DiffViewer
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        diffData={MOCK_DIFF_DATA}
        loading={false}
        prTitle={pr.title}
        prNumber={pr.prNumber}
        externalUrl={pr.externalUrl}
        gitProvider={pr.gitProvider}
      />
    </>
  );
}

function PullRequests() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [prs, setPRs] = useState([]);
  const [gitConnections] = useState(MOCK_GIT_CONNECTIONS);
  const [activeTab, setActiveTab] = useState('all');
  const [isOpenPR, setOpenPRModal] = useState(false);
  const [isOpenGit, setOpenGitModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState();
  const [prForm] = KoForm.useForm();
  const [gitForm] = KoForm.useForm();

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath },
      { title: 'Pull Requests' }
    ]));
    setPRs(MOCK_PRS);
  }, [workspaceId]);

  const handleStatusUpdate = (prId, newStatus) => {
    setPRs((prev) => prev.map((p) => p.prId === prId ? { ...p, status: newStatus } : p));
  };

  const handleCreatePR = (values) => {
    values['prId'] = KoNano('PR');
    values['prNumber'] = Math.floor(Math.random() * 900) + 100;
    values['status'] = 'open';
    values['authorName'] = 'You';
    values['filesChanged'] = 0;
    values['additions'] = 0;
    values['deletions'] = 0;
    values['idleDays'] = 0;
    values['reviewers'] = [];
    values['governanceChecks'] = {};
    values['automatedChecks'] = {};
    setPRs((prev) => [...prev, values]);
    setOpenPRModal(false);
    prForm.resetFields();
    KoToast.success('PR opened — reviewers notified!');
  };

  const handleConnectGit = () => {
    setOpenGitModal(false);
    gitForm.resetFields();
    KoToast.success('Git provider connected!');
  };

  const filterByTab = (tab) => {
    const tabMap = {
      open: ['open', 'draft'],
      review: ['in_review', 'changes_requested'],
      merged: ['merged'],
      all: null
    };
    const statuses = tabMap[tab];
    return statuses ? prs.filter((p) => statuses.includes(p.status)) : prs;
  };

  const filteredPRs = filterByTab(activeTab);

  const tabItems = [
    { key: 'open', label: `Open (${prs.filter(p => ['open', 'draft'].includes(p.status)).length})` },
    { key: 'review', label: `In Review (${prs.filter(p => ['in_review', 'changes_requested'].includes(p.status)).length})` },
    { key: 'merged', label: `Merged (${prs.filter(p => p.status === 'merged').length})` },
    { key: 'all', label: `All (${prs.length})` }
  ];

  return (
    <div className='tableCard'>
      <ReleaseSubNav activeKey='pull-requests' />

      <KoToolBar
        title='Pull Requests'
        search={{
          visible: true,
          onChange: () => {},
          placeholder: 'Search PRs by title or ticket'
        }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          {
            label: 'Git Connected',
            icon: <KoIcon color='#00a76f' name='CheckCircleOutlined' width={16} height={16} />,
            onClick: () => setOpenGitModal(true)
          },
          {
            label: 'Open PR',
            type: 'primary',
            icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
            onClick: () => { setCurrentRecord(); setOpenPRModal(true); }
          }
        ]}
      />

      <div className={styles.flowBanner}>
        <KoIcon name='PullRequestOutlined' width={14} height={14} />
        <KoText text='Step 1: Code Review — Merge all PRs before advancing to QA Testing' />
      </div>

      <div className={styles.connectionChips}>
        {gitConnections.map((conn) => (
          <div key={conn.connectionId} className={styles.connChip}>
            <KoIcon name={`${conn.provider}Outlined`} width={14} height={14} />
            <KoText text={conn.name || conn.provider} />
            <KoTag color='success'>Connected</KoTag>
          </div>
        ))}
      </div>

      <div className={styles.tabsWrap}>
        <KoTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </div>

      <div className={styles.prList}>
        {filteredPRs.length === 0 ? (
          <KoEmpty description='No pull requests in this stage' />
        ) : (
          filteredPRs.map((pr) => (
            <PullRequestCard
              key={pr.prId}
              pr={pr}
              onRefresh={handleStatusUpdate}
            />
          ))
        )}
      </div>

      <KoModal
        title='Open Pull Request'
        centered
        open={isOpenPR}
        afterClose={() => setCurrentRecord()}
        handleOk={() => setOpenPRModal(false)}
        onClose={() => setOpenPRModal(false)}
        width={'min(760px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenPRModal(false)} />,
          <KoButton key='create' type='primary' label='Open PR' onClick={() => prForm.submit()} />
        ]}
      >
        <PRCrud form={prForm} initialValues={currentRecord} handleFinish={handleCreatePR} reviewerOptions={[
          { label: 'Alice Park', value: 'alice' },
          { label: 'Bob Chen', value: 'bob' },
          { label: 'Sarah Kim', value: 'sarah' },
          { label: 'John Doe', value: 'john' }
        ]} />
      </KoModal>

      <KoModal
        title='Connect Git Provider'
        centered
        open={isOpenGit}
        handleOk={() => setOpenGitModal(false)}
        onClose={() => setOpenGitModal(false)}
        width={'min(600px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setOpenGitModal(false)} />,
          <KoButton key='connect' type='primary' label='Save Connection' onClick={() => gitForm.submit()} />
        ]}
      >
        <GitProviderCrud form={gitForm} handleFinish={handleConnectGit} onOAuthConnect={() => setOpenGitModal(false)} />
      </KoModal>
    </div>
  );
}

export default PullRequests;
